import { z } from "zod";
import { UserRoleSchema } from "./enums.js";
import { INDEXED_STRING_MAX, PhoneSchema } from "./primitives.js";
/**
 * Auth request/response contracts (spec §6 `/auth/*`, §10 login UX).
 *
 * Two sign-in paths, because there are exactly two humans (spec §2):
 *   OWNER — the father — phone + 6-digit PIN on a large numeric keypad (§10).
 *   ADMIN — the son    — email + password.
 *
 * These schemas live here, not in `apps/api`, so the SPA's React Hook Form
 * resolver and the server's `validate()` middleware enforce the identical rule
 * (spec §11A: "Input validation server-side (zod) and client-side, sharing
 * schemas from packages/types").
 *
 * Nothing in this file ever describes a hash. `pin_hash` / `password_hash` are
 * server-side columns and never cross the wire in either direction (§3, §14.2).
 */
/* -------------------------------------------------------------------------- */
/* PIN                                                                         */
/* -------------------------------------------------------------------------- */
/** A PIN is exactly six digits — §3 ("pin_hash (bcrypt, 6-digit)"), §10. */
export const PIN_LENGTH = 6;
/**
 * Kept as a STRING, never a number: `012345` is a legitimate PIN and
 * `Number("012345")` is 12345, which would silently make a leading-zero PIN
 * unloggable-in. The numeric keypad in the SPA (§10) produces a string too.
 */
export const PinSchema = z
    .string()
    .trim()
    .regex(new RegExp(`^\\d{${PIN_LENGTH}}$`), `PIN must be exactly ${PIN_LENGTH} digits`);
/* -------------------------------------------------------------------------- */
/* Password (ADMIN)                                                            */
/* -------------------------------------------------------------------------- */
/**
 * bcrypt hashes at most the first 72 BYTES of an input and silently ignores the
 * rest, so anything longer would give two different passwords the same hash.
 * Refuse at the edge rather than truncate.
 */
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX_BYTES = 72;
export const PasswordSchema = z
    .string()
    .min(PASSWORD_MIN, `Password must be at least ${PASSWORD_MIN} characters`)
    .refine((value) => new TextEncoder().encode(value).length <= PASSWORD_MAX_BYTES, `Password must be ${PASSWORD_MAX_BYTES} bytes or fewer`);
/* -------------------------------------------------------------------------- */
/* Email (ADMIN)                                                               */
/* -------------------------------------------------------------------------- */
/**
 * Lower-cased on the way in so `Admin@example.com` and `admin@example.com` are
 * the same login. The column is `@db.VarChar(191)` and unique, hence the cap.
 */
export const EmailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(1, "Email is required")
    .max(INDEXED_STRING_MAX, `Email must be ${INDEXED_STRING_MAX} characters or fewer`)
    .email("Enter a valid email address");
/* -------------------------------------------------------------------------- */
/* Requests                                                                    */
/* -------------------------------------------------------------------------- */
/** `POST /auth/owner-login` — { phone, pin } (spec §6). */
export const OwnerLoginSchema = z
    .object({
    phone: PhoneSchema,
    pin: PinSchema,
})
    .strict();
/** `POST /auth/admin-login` — { email, password } (spec §6). */
export const AdminLoginSchema = z
    .object({
    email: EmailSchema,
    password: PasswordSchema,
})
    .strict();
/**
 * `POST /auth/refresh`.
 *
 * The refresh token travels in the JSON body rather than the `Authorization`
 * header, because the header is reserved for ACCESS tokens — a refresh token
 * presented there must never authenticate a request (see apps/api auth/tokens).
 */
export const RefreshSchema = z
    .object({
    refreshToken: z.string().trim().min(1, "Refresh token is required"),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Responses                                                                   */
/* -------------------------------------------------------------------------- */
/**
 * The public shape of a signed-in user. This is the ONLY user shape the API is
 * ever allowed to serialise: no phone, no email, no hash, no counters.
 */
export const AuthUserSchema = z.object({
    id: z.number().int().positive(),
    role: UserRoleSchema,
    displayName: z.string(),
});
/** Body returned by all three `/auth/*` endpoints. */
export const AuthTokensSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    /** Access-token lifetime in SECONDS (not ms, not an absolute timestamp). */
    expiresIn: z.number().int().positive(),
    user: AuthUserSchema,
});
//# sourceMappingURL=auth.js.map